import {Injectable} from "@angular/core";
import {NavigationEnd, Router} from "@angular/router";
import {SwUpdate, VersionEvent} from "@angular/service-worker";
import {fromEvent, merge} from "rxjs";
import {distinctUntilChanged, filter, map, skip} from "rxjs/operators";
import {CONSTANT} from "../../home-layout/common/constant/constant";

const UNSAVED_WORK_SELECTOR = '.ng-dirty, .mat-mdc-dialog-container, [contenteditable="true"], .monaco-editor';

@Injectable({
    providedIn: "root"
})
export class AppUpdateService {

    private isNewVersionReady: boolean = false;
    private isVersionCheckInFlight: boolean = false;

    constructor(private router: Router,
                private swUpdate: SwUpdate) {
    }

    watchForNewRelease(): void {
        if (!this.swUpdate.isEnabled) {
            this.removeLeftoverServiceWorker();
            return;
        }

        this.downloadNewVersionWhenTabReturns();
        this.downloadNewVersionWhileNavigating();
        this.reloadIfThisTabIsBehind();
        this.subscribeToNewVersionReady();
        this.reloadWhenSafeAndNewVersionReady();
    }

    private removeLeftoverServiceWorker(): void {
        navigator.serviceWorker?.getRegistrations()
            .then(serviceWorkerRegistrations => this.unregisterAppServiceWorkers(serviceWorkerRegistrations))
            .catch(() => false);
    }

    private unregisterAppServiceWorkers(serviceWorkerRegistrations: readonly ServiceWorkerRegistration[]): void {
        serviceWorkerRegistrations
            .filter(serviceWorkerRegistration => this.isAppServiceWorker(serviceWorkerRegistration))
            .forEach(serviceWorkerRegistration => serviceWorkerRegistration.unregister());
    }

    private isAppServiceWorker(serviceWorkerRegistration: ServiceWorkerRegistration): boolean {
        return serviceWorkerRegistration.active?.scriptURL.endsWith(CONSTANT.SERVICE_WORKER_SCRIPT) ?? false;
    }

    private downloadNewVersionWhenTabReturns(): void {
        fromEvent(document, 'visibilitychange').pipe(
            filter(() => !document.hidden)
        ).subscribe(() => this.downloadNewVersion());
    }

    private downloadNewVersionWhileNavigating(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => this.downloadNewVersion());
    }

    private downloadNewVersion(): void {
        if (this.isVersionCheckInFlight) {
            return;
        }

        this.isVersionCheckInFlight = true;
        this.swUpdate.checkForUpdate()
            .catch(() => false)
            .finally(() => this.isVersionCheckInFlight = false);
    }

    private reloadIfThisTabIsBehind(): void {
        this.swUpdate.activateUpdate()
            .catch(() => false)
            .then(isNewVersionActivated => this.reloadIfActivated(isNewVersionActivated));
    }

    private subscribeToNewVersionReady(): void {
        this.swUpdate.versionUpdates.pipe(
            filter((versionEvent: VersionEvent) => versionEvent.type === 'VERSION_READY')
        ).subscribe(() => this.isNewVersionReady = true);
    }

    private reloadWhenSafeAndNewVersionReady(): void {
        merge(
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd),
                map(() => this.toScreenPath(this.router.url)),
                distinctUntilChanged(),
                skip(1)
            ),
            fromEvent(document, 'visibilitychange').pipe(filter(() => document.hidden))
        ).pipe(
            filter(() => this.isNewVersionReady && !this.hasUnsavedEdits())
        ).subscribe(() => document.location.reload());
    }

    private reloadIfActivated(isNewVersionActivated: boolean): void {
        if (!isNewVersionActivated) {
            return;
        }

        document.location.reload();
    }

    private hasUnsavedEdits(): boolean {
        return document.querySelector(UNSAVED_WORK_SELECTOR) !== null;
    }

    private toScreenPath(url: string): string {
        return url.split('?')[0];
    }
}
